import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { AtDoorByType } from "building-types/electrical/electricalLayoutTypes";
import { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import {
  ControllerSelectionType,
  ControllerSwitchType,
  ControllerType,
} from "building-types/electrical/electricalTypesEnum";
import type { Controller, GeneratedElectricalGroup, Load } from "building-types/electrical/generatedElectricalTypes";
import { WallType } from "building-types/walls/wallEnums";
import { ensureDefined } from "common/utils/assertOfType";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { aabb2ByPoints, aabb2Center } from "geom/aabb2";
import { polyLine2EdgeEvalOffset } from "geom/polyLine2";
import type { Vec2 } from "geom/vec2";
import { vec2Copy } from "geom/vec2";
import { commitControllerSelection } from "../controllerSelection/controllerSelectionCommit";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";

export function atDoorByType({
  controllerPlacementType = [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  loadType,
  doorType,
  offset,
  connected = false,
}: AtDoorByType): ElectricalLayoutFunc {
  return async (opts: ElectricalLayoutOpts) => {
    const { room, wallIndex, controllerSelectionFunc, electricalItemCatalogSettings } = opts;
    const resolvedLoadType = loadType;
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[resolvedLoadType];
    const walls = wallIndex.wallsInRoom(room);
    const loadLocs: Vec2[] = [];
    for (const wall of walls) {
      if (wall.wallType !== WallType.SOLID) {
        continue;
      }
      for (const door of wall.doorPlacements) {
        if ((doorType && door.descriptor.id === doorType) || !doorType) {
          const [dx, dy] = polyLine2EdgeEvalOffset(
            wallOpeningPlanExtents(ensureDefined(door.opening)),
            door.frontRoom.id === room.id ? 0 : 2,
            offset,
            0.5,
            true,
          );
          loadLocs.push([dx, dy]);
        }
      }
    }

    const loads: Load[] = [];
    for (const loc of loadLocs) {
      loads.push({
        loadType: resolvedLoadType,
        loadDescriptor,
        position: vec2Copy(loc),
        normal: [0, 1],
        levelId: room.level.id,
        sourceRoomHandle: room.roomHandle,
        hideConnections: !connected,
      });
    }

    if (loadLocs.length > 0) {
      const lightsBB = aabb2ByPoints(loadLocs);
      const lightCenter = aabb2Center(lightsBB);
      const candidate = await controllerSelectionFunc(controllerPlacementType, lightCenter);
      const controllerType = ControllerType.SWITCH;
      const controllerPosition = commitControllerSelection(
        candidate,
        ElectricalLayoutType.AT_BACK_OF_PROP,
        controllerType,
        lightCenter,
      );

      if (!controllerPosition) {
        const lightElectricalGroup: GeneratedElectricalGroup = {
          levelId: opts.room.level.id,
          loads,
          controllers: [],
        };

        return [lightElectricalGroup];
      }

      const controller: Controller = {
        type: controllerType,
        switchType: ControllerSwitchType.STANDARD,
        position: controllerPosition,
        loadIndex: 0,
        candidateId: candidate?.candidate?.id,
        levelId: room.level.id,
        sourceRoomHandle: room.roomHandle,
        labels: makeBuiltInLabelTable("addToElectricalPlan"),
      };

      const electricalGroups: GeneratedElectricalGroup[] = [];

      if (connected) {
        const lightElectricalGroup: GeneratedElectricalGroup = {
          levelId: opts.room.level.id,
          loads,
          controllers: [controller],
        };

        electricalGroups.push(lightElectricalGroup);
      } else {
        for (const load of loads) {
          electricalGroups.push({
            levelId: opts.room.level.id,
            loads: [
              {
                loadType: resolvedLoadType,
                loadDescriptor,
                position: load.position,
                normal: load.normal,
                levelId: room.level.id,
                sourceRoomHandle: room.roomHandle,
                hideConnections: true,
              },
            ],
            controllers: [],
          });
        }
      }

      return electricalGroups;
    }

    return null;
  };
}
